import { useCallback, useMemo } from 'react';
import find from 'lodash/find';
import isArray from 'lodash/isArray';
import map from 'lodash/map';
import uniqBy from 'lodash/uniqBy';

import {
  MultiSelectChangeCallbackType,
  MultiSelectDataType
} from '../../../../helpers/MultiSelect/types';
import { MultiSelect, MultiSelectProps } from '../../../../helpers/MultiSelect';
import {
  CacheGroupedItemsGroupByEnum,
  FetchCacheGroupedItemsFilters,
  FetchGroupedItemsCacheKey,
  GroupedItemItemName
} from '../../groupedItemsTypes';

import {
  FETCH_SELECT_GROUPED_ITEMS_QUERY,
  FetchSelectGroupedItemsQueryResponse
} from '../../queries/fetchSelectGroupedItems.query';

import { useFinPaginatedGroupedItems } from '../../hooks/useFinPaginatedGroupedItems';
import { useCacheGroupedItems } from '../../hooks/useCacheGroupedItems';
import { CacheFetchPaymentsGroupedItemsQueryResponse } from '../../queries/fetchCachePaymentsGroupedItems.query';
import {
  CACHE_FETCH_SELECT_GROUPED_ITEMS_QUERY,
  CacheFetchSelectGroupedItemsQueryResponse
} from '../../queries/fetchCacheSelectGroupedItems.query';

interface GroupedItemsMultiSelectProps {
  value: GroupedItemItemName[];
  autoFocus?: boolean;
  onChange: (value: GroupedItemItemName[]) => void;
  cacheKey: FetchGroupedItemsCacheKey;
  initialFilters?: FetchCacheGroupedItemsFilters;
}

const groupedItemsInitialLimit = 1000;

function GroupedItemsMultiSelect({
  value,
  autoFocus,
  onChange,
  cacheKey,
  initialFilters
}: GroupedItemsMultiSelectProps & MultiSelectProps) {
  const { groupedItems, groupedItemsError, groupedItemsLoading } =
    useCacheGroupedItems<CacheFetchSelectGroupedItemsQueryResponse>({
      additionalParams: {
        groupBy: [
          CacheGroupedItemsGroupByEnum.ITEM,
          CacheGroupedItemsGroupByEnum.TASK
        ],
        groupItemsByType: true
      },
      cacheKey,
      cacheQuery: CACHE_FETCH_SELECT_GROUPED_ITEMS_QUERY,
      initialFilters,
      initialLimit: groupedItemsInitialLimit,
      query: CACHE_FETCH_SELECT_GROUPED_ITEMS_QUERY,
      trackTotalHits: true
    });

  const itemsData = useMemo<MultiSelectDataType[]>(() => {
    return uniqBy(
      map(groupedItems, (item) => ({
        value: item.itemName,
        label: item.itemName
      })),
      'value'
    );
  }, [groupedItems]);

  const handleChange = useCallback<MultiSelectChangeCallbackType>(
    (value: MultiSelectDataType) => {
      onChange(isArray(value) ? value.map((item) => item.value) : []);
    },
    [onChange]
  );

  const multiSelectValue = useMemo<MultiSelectDataType[]>(() => {
    return isArray(value)
      ? value.map((item) => find(itemsData, ['value', item]))
      : [];
  }, [value, itemsData]);

  return (
    <MultiSelect
      error={groupedItemsError}
      inputWrapperClassName="block"
      onChange={handleChange}
      value={multiSelectValue}
      multi={true}
      isSearchable={true}
      optionsLoading={groupedItemsLoading}
      isLoading={groupedItemsLoading}
      data={itemsData}
      classNamePrefix="av_multi_select"
      autoFocus={autoFocus}
    />
  );
}

export default GroupedItemsMultiSelect;
